import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Search, Save, RotateCcw, Edit2, Check, X, Building, Layers, DollarSign, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanConfiguration = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [editingPlan, setEditingPlan] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [message, setMessage] = useState(null);

  // Revenue breakdown categories
  const revenueCategories = [
    'Management Fees',
    'OC Disbursements Recovered',
    'OC Fee for Service',
    'Chargeable Meetings',
    'Tax Compliance Preparation',
    'Debt Recovery Admin Charge',
    'OC Certificate application charge',
    'OC Insurance Commission'
  ];

  useEffect(() => {
    fetchPlanConfig();
  }, []);

  const fetchPlanConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/plan-config`);
      const data = await response.json();
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Error fetching plan config:', error);
      setMessage({ type: 'error', text: 'Failed to load plan configurations' });
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = useMemo(() => {
    if (!filter) return plans;
    const searchLower = filter.toLowerCase();
    return plans.filter(p => 
      p.planNumber.toLowerCase().includes(searchLower) ||
      (p.propertyName && p.propertyName.toLowerCase().includes(searchLower))
    );
  }, [plans, filter]);

  const startEditing = (plan) => {
    setEditingPlan(plan.planNumber);
    setEditValues({
      propertyName: plan.propertyName || '',
      occupiableLots: plan.occupiableLots || 0,
      managementFees: plan.managementFees || 0,
      totalRevenue: plan.totalRevenue || 0,
      revenueBreakdown: { ...plan.revenueBreakdown }
    });
  };

  const cancelEditing = () => {
    setEditingPlan(null);
    setEditValues({});
  };

  const saveChanges = async (planNumber) => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/plan-config/${planNumber}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editValues)
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      setMessage({ type: 'success', text: `Plan ${planNumber} updated successfully` });
      setEditingPlan(null);
      fetchPlanConfig(); // Refresh data
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to update plan ${planNumber}` });
    } finally {
      setSaving(false);
    }
  };

  const resetPlan = async (planNumber) => {
    if (!window.confirm(`Reset plan ${planNumber} to original values?`)) return;
    
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/plan-config/${planNumber}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to reset');
      
      setMessage({ type: 'success', text: `Plan ${planNumber} reset to defaults` });
      fetchPlanConfig();
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to reset plan ${planNumber}` });
    } finally {
      setSaving(false);
    }
  };

  // Auto-hide message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (loading) {
    return (
      <div className={`p-8 ${isDark ? 'bg-[#0a0a0b]' : 'bg-slate-50'} min-h-screen flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className={`p-8 ${isDark ? 'bg-[#0a0a0b]' : 'bg-slate-50'} min-h-screen`}>
      {/* Header */}
      <div className="mb-8">
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Plan Master List Configuration
        </h1>
        <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          Configure plan properties, occupiable lots, and revenue categories. Changes override uploaded data.
        </p>
      </div>

      {/* Message Toast */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {message.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Search and Stats */}
      <div className={`rounded-2xl border p-6 mb-6 ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <input
              type="text"
              placeholder="Search by plan number or property name..."
              className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-1 focus:ring-emerald-500/30 focus:border-emerald-500/50 transition-all
                ${isDark ? 'bg-white/5 border-white/5 text-white placeholder-slate-500' : 'bg-slate-100 border-slate-200 text-slate-900 placeholder-slate-400'}`}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-4">
            <div className={`px-4 py-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
              <span className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Plans</span>
              <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{plans.length}</p>
            </div>
            <div className={`px-4 py-2 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
              <span className={`text-xs font-medium text-emerald-500`}>Custom Overrides</span>
              <p className={`text-lg font-bold text-emerald-500`}>{plans.filter(p => p.hasOverride).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Plans Table */}
      <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200'}`}>
        <div className="table-scroll-wrapper">
          <div className="table-scroll-container">
            <table className="w-full text-sm text-left min-w-[1400px]">
              <thead className={`text-xs uppercase tracking-wider ${isDark ? 'bg-white/[0.02] text-slate-500' : 'bg-slate-50 text-slate-400'}`}>
                <tr>
                  <th className="px-4 py-4 font-semibold sticky left-0 z-10" style={{ backgroundColor: isDark ? '#111113' : '#f8fafc' }}>Plan #</th>
                  <th className="px-4 py-4 font-semibold">Property Name</th>
                  <th className="px-4 py-4 font-semibold text-center">Lots</th>
                  <th className="px-4 py-4 font-semibold text-right">Mgmt Fee</th>
                  <th className="px-4 py-4 font-semibold text-right">Total Revenue</th>
                  <th className="px-4 py-4 font-semibold text-right">OC Disb.</th>
                  <th className="px-4 py-4 font-semibold text-right">Fee for Service</th>
                  <th className="px-4 py-4 font-semibold text-right">Meetings</th>
                  <th className="px-4 py-4 font-semibold text-right">Insurance</th>
                  <th className="px-4 py-4 font-semibold text-center">Status</th>
                  <th className="px-4 py-4 font-semibold text-center">Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
                {filteredPlans.map((plan) => (
                  <tr key={plan.planNumber} className={`transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'}`}>
                    {editingPlan === plan.planNumber ? (
                      // Editing Row
                      <>
                        <td className="px-4 py-3 font-semibold sticky left-0 z-10" style={{ backgroundColor: isDark ? '#111113' : 'white' }}>
                          <span className={isDark ? 'text-white' : 'text-slate-900'}>{plan.planNumber}</span>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editValues.propertyName}
                            onChange={(e) => setEditValues({ ...editValues, propertyName: e.target.value })}
                            className={`w-full px-2 py-1.5 text-sm border rounded-lg ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                            placeholder="Property Name"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={editValues.occupiableLots}
                            onChange={(e) => setEditValues({ ...editValues, occupiableLots: parseInt(e.target.value) || 0 })}
                            className={`w-20 px-2 py-1.5 text-sm border rounded-lg text-center ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={editValues.managementFees}
                            onChange={(e) => setEditValues({ ...editValues, managementFees: parseFloat(e.target.value) || 0 })}
                            className={`w-28 px-2 py-1.5 text-sm border rounded-lg text-right ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={editValues.totalRevenue}
                            onChange={(e) => setEditValues({ ...editValues, totalRevenue: parseFloat(e.target.value) || 0 })}
                            className={`w-28 px-2 py-1.5 text-sm border rounded-lg text-right ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={editValues.revenueBreakdown?.['OC Disbursements Recovered'] || 0}
                            onChange={(e) => setEditValues({
                              ...editValues,
                              revenueBreakdown: { ...editValues.revenueBreakdown, 'OC Disbursements Recovered': parseFloat(e.target.value) || 0 }
                            })}
                            className={`w-24 px-2 py-1.5 text-sm border rounded-lg text-right ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={editValues.revenueBreakdown?.['OC Fee for Service'] || 0}
                            onChange={(e) => setEditValues({
                              ...editValues,
                              revenueBreakdown: { ...editValues.revenueBreakdown, 'OC Fee for Service': parseFloat(e.target.value) || 0 }
                            })}
                            className={`w-24 px-2 py-1.5 text-sm border rounded-lg text-right ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={editValues.revenueBreakdown?.['Chargeable Meetings'] || 0}
                            onChange={(e) => setEditValues({
                              ...editValues,
                              revenueBreakdown: { ...editValues.revenueBreakdown, 'Chargeable Meetings': parseFloat(e.target.value) || 0 }
                            })}
                            className={`w-24 px-2 py-1.5 text-sm border rounded-lg text-right ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={editValues.revenueBreakdown?.['OC Insurance Commission'] || 0}
                            onChange={(e) => setEditValues({
                              ...editValues,
                              revenueBreakdown: { ...editValues.revenueBreakdown, 'OC Insurance Commission': parseFloat(e.target.value) || 0 }
                            })}
                            className={`w-24 px-2 py-1.5 text-sm border rounded-lg text-right ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="px-2 py-1 text-xs font-medium rounded-lg bg-amber-500/10 text-amber-500">
                            Editing
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => saveChanges(plan.planNumber)}
                              disabled={saving}
                              className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-colors"
                              title="Save"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className={`p-1.5 rounded-lg ${isDark ? 'bg-white/5 text-slate-400 hover:bg-white/10' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'} transition-colors`}
                              title="Cancel"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      // Display Row
                      <>
                        <td className="px-4 py-3 font-semibold sticky left-0 z-10" style={{ backgroundColor: isDark ? '#111113' : 'white' }}>
                          <span className={isDark ? 'text-white' : 'text-slate-900'}>{plan.planNumber}</span>
                        </td>
                        <td className={`px-4 py-3 max-w-[200px] truncate ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          {plan.propertyName || <span className="text-slate-400 italic">-</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${plan.occupiableLots > 0 ? 'bg-cyan-500/10 text-cyan-600' : isDark ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400'}`}>
                            {plan.occupiableLots || '-'}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          ${plan.managementFees?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono text-xs font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          ${plan.totalRevenue?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          ${plan.revenueBreakdown?.['OC Disbursements Recovered']?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          ${plan.revenueBreakdown?.['OC Fee for Service']?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          ${plan.revenueBreakdown?.['Chargeable Meetings']?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          ${plan.revenueBreakdown?.['OC Insurance Commission']?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {plan.hasOverride ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-lg bg-emerald-500/10 text-emerald-500">
                              Custom
                            </span>
                          ) : (
                            <span className={`px-2 py-1 text-xs font-medium rounded-lg ${isDark ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400'}`}>
                              Default
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => startEditing(plan)}
                              className={`p-1.5 rounded-lg ${isDark ? 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'} transition-colors`}
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {plan.hasOverride && (
                              <button
                                onClick={() => resetPlan(plan.planNumber)}
                                className="p-1.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
                                title="Reset to defaults"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className={`px-4 py-3 border-t text-xs font-medium text-center uppercase tracking-wider
          ${isDark ? 'border-white/5 bg-white/[0.01] text-slate-500' : 'border-slate-200 bg-slate-50 text-slate-400'}`}>
          Showing {filteredPlans.length} of {plans.length} Plans
        </div>
      </div>
    </div>
  );
};

export default PlanConfiguration;
